<template>
  <div class="language-list" role="listbox" :aria-label="$t('languageSwitcher.switchTo')">
    <button
      v-for="code in heldLanguages"
      :key="code"
      class="language-row"
      data-test="sheet-language"
      :data-code="code"
      role="option"
      :aria-current="code === displayedLanguage ? 'true' : 'false'"
      :class="{ active: code === displayedLanguage }"
      @click="$emit('pick', code)"
    >
      <img :src="flagsStore.getFlag(code)" :alt="languageAlt(code)" class="row-flag" />
      <span class="row-name">{{ languageName(code) }}</span>
      <font-awesome-icon
        v-if="code === displayedLanguage"
        icon="fa-solid fa-check"
        class="row-check"
      />
    </button>
    <button class="language-row add-row" data-test="sheet-add" @click="$emit('add')">
      <span class="add-badge"><font-awesome-icon icon="fa-solid fa-plus" /></span>
      <span class="row-name">{{ $t('languageSwitcher.addLanguage') }}</span>
    </button>
  </div>
</template>

<script>
import { flagsStore } from "@/store/countryFlags.js";
import Languages from "@/data/Language.js";

// Presentational list for the mobile language sheet. The owner (BottomTabBar)
// holds the useLearningLanguages state and decides what `pick`/`add` do.
export default {
  name: "LanguageSheetContent",
  props: {
    heldLanguages: {
      type: Array,
      required: true,
    },
    displayedLanguage: {
      type: String,
      default: null,
    },
  },
  emits: ["pick", "add"],
  setup() {
    const languageName = (code) => Languages[code]?.nativeName || code;
    const languageAlt = (code) => Languages[code]?.altText || "Country flag";
    return { flagsStore, languageName, languageAlt };
  },
};
</script>

<style scoped>
.language-list {
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
}

.language-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-radius: var(--al-radius);
  cursor: pointer;
  color: var(--al-text);
  font-size: 16px;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.language-row:active,
.language-row:focus-visible {
  background: var(--al-surface-sunken);
}

.language-row.active {
  background: var(--al-primary-soft);
  font-weight: 600;
}

.row-flag {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.row-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-check {
  color: var(--al-primary);
  flex-shrink: 0;
}

.add-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--al-primary-soft);
  color: var(--al-primary);
  font-size: 12px;
  flex-shrink: 0;
}
</style>
